import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, IconButton} from '@mui/material';
import classNames from 'classnames';
import moment from 'moment';
import {FormattedMessage, IntlShape, useIntl} from 'react-intl';
import {useMediaQuery} from 'react-responsive';
import {useNavigate} from 'react-router-dom';
import {SVGS} from '../../assets/images';
import ScheduleDateSelector from '../../components/Schedule/header/components/schedule-date-selector';
import PageHeader from '../../components/shared/page-header';
import PageFooter from '../../components/shared/page-footer';
import CreateNewButtonV2 from '../../components/shared/create-new-button-v2';
import {CollapseButton} from '../../components/shared/collapse-button';
import {DeleteDialog} from '../../components/shared/delete-dialog';
import {DeleteIconButton} from '../../components/shared/delete-icon-button';
import TimiDataGrid from '../../components/shared/grid';
import {GridColumnProps, GridGroupByProps, GridRef} from '../../components/shared/grid/types';
import PageWrapperV2 from '../../components/shared/page-wrapper-v2';
import SearchInput from '../../components/shared/search-input';
import TimeTrackingPageBody from '../../components/shared/time-tracking-page-body';
import {getDefaultMonthRange, getSelectedDateRangeFromFilter, mapWorkCalendarYears} from '../../components/shared/calendar/utils';
import {RangeType} from '../../components/shared/calendar/types';
import Selector from '../../components/shared/nextgen/selector';
import {ActionCancelButtonsArea} from '../../components/shared/action-cancel-buttons-area';
import HttpClient from '../../api/api';
import {getAllEmployees} from '../../features/employees/short';
import {setIndicatorLoading} from '../../features/indicator';
import {
    canManageOtherTimeBank,
    canSeeOtherTimeBank,
    canSeeTimeBankReportPage,
} from '../../features/profile/entityPermissions';
import {REmployees} from '../../features/roles/types';
import {getWorkCalendar, getWorkCalendars} from '../../features/settings/workcalendar';
import {Year} from '../../features/settings/workcalendar/types';
import {updateTimeTrackingDateRangeFilter, updateTimeTrackingPropertyFilter} from '../../features/time-tracking-filters';
import {
    TimeBankTransaction,
    TimeBankTransactionSourceType,
    TimeBankTransactionType,
} from '../../features/time-bank/types';
import {useAppDispatch, useAppSelector} from '../../hooks/hooks';
import {Lx} from '../../i18n/consts';
import {NAVIGATION_ROUTES} from '../../routes/NavigationRoute';
import styles from './time-bank-report.module.scss';
import costTopBarStyles from '../CostReport/components/top-bar/cost-report-top-bar.module.scss';
import totalStyles from '../../components/Schedule/total-v2/total.module.scss';
import TimeBankFilter, {
    emptyTimeBankFilterState,
    normalizeTimeBankFilterState,
    TimeBankFilterState,
} from './time-bank-filter';

const ALLOWED_RANGE_TYPES = [
    RangeType.CurrentMonth,
    RangeType.PreviousMonth,
    RangeType.ThisWeek,
    RangeType.LastWeek,
    RangeType.Custom,
];

const formatHours = (value: number): string => {
    const fixed = Number(value.toFixed(2));
    return `${fixed > 0 ? '+' : ''}${fixed} h`;
};

const formatTableHours = (value: number): string => {
    if (value === 0) {
        return '';
    }

    const fixed = Number(value.toFixed(2));
    return `${fixed > 0 ? '+' : ''}${fixed}`;
};

const formatGroupHours = (value: number): string => {
    const fixed = Number(value.toFixed(2));
    return `${fixed > 0 ? '+' : ''}${fixed}`;
};

const transactionTypeLabels: Record<TimeBankTransactionType, string> = {
    [TimeBankTransactionType.InitialBalance]: 'Initial balance',
    [TimeBankTransactionType.PutInto]: 'Put into time bank',
    [TimeBankTransactionType.TakenFrom]: 'Taken from time bank',
    [TimeBankTransactionType.ManualAdjustment]: 'Manual adjustment',
};

const sourceTypeLabels: Record<TimeBankTransactionSourceType, string> = {
    [TimeBankTransactionSourceType.Migration]: 'Migration',
    [TimeBankTransactionSourceType.Shift]: 'Shift',
    [TimeBankTransactionSourceType.Absence]: 'Absence',
    [TimeBankTransactionSourceType.ManualAdjustment]: 'Manual adjustment',
};

type TimeBankBalanceRow = {
    employeeId: number;
    employeeName: string;
    balance: number;
    openingBalance: number;
    periodBalance: number;
    transactionCount: number;
    transactions: TimeBankTransactionRow[];
};

type TimeBankTransactionRow = TimeBankTransaction & {
    employeeId: number;
    employeeName: string;
};

type TimeBankGridRow = Omit<TimeBankTransactionRow, 'id'> & {
    id: string;
    transactionId: number | null;
    employeeGroupKey: string;
    balanceRow: TimeBankBalanceRow;
    isPlaceholder: boolean;
};

const TimeBankReportTitle = ({className}: { className: string }) => (
    <div className={costTopBarStyles.titleWithSpinner}>
        <div className={className}>
            <FormattedMessage id='timebankreport' />
        </div>
    </div>
);

function filterTimeBankBalances(balances: TimeBankBalanceRow[], search: string): TimeBankBalanceRow[] {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
        return balances;
    }

    return balances.filter((balance) => {
        if (balance.employeeName.toLowerCase().includes(normalizedSearch)) {
            return true;
        }

        return balance.transactions.some((transaction) => {
            const type = transactionTypeLabels[transaction.type] ?? String(transaction.type);
            const source = sourceTypeLabels[transaction.sourceType] ?? String(transaction.sourceType);
            const values = [
                moment(transaction.date).format('YYYY-MM-DD'),
                formatHours(transaction.hours),
                type,
                source,
                transaction.comment ?? '',
            ];

            return values.some((value) => value.toLowerCase().includes(normalizedSearch));
        });
    });
}

export default function TimeBankReportPage() {
    const today = useMemo(() => moment(), []);
    const currentYear = today.year();
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const profile = useAppSelector((selector) => selector.profile);
    const employees = useAppSelector((selector) => selector.rEmployees);
    const {workCalendars} = useAppSelector((selector) => selector.workCalendar);
    const dateRangeFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter?.dateRangeFilter);
    const propertyFilter = useAppSelector((selector): Partial<TimeBankFilterState> => selector.timeTrackingFilters.filter?.propertyFilter ?? emptyTimeBankFilterState);
    const filterState = useMemo(() => normalizeTimeBankFilterState(propertyFilter), [propertyFilter]);
    const [years, setYears] = useState<Year[]>([]);
    const [balances, setBalances] = useState<TimeBankBalanceRow[]>([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [transactionPendingDelete, setTransactionPendingDelete] = useState<TimeBankTransactionRow | null>(null);
    const [isDateSelectorOpen, setDateSelectorOpen] = useState(false);
    const [isFilterModalOpen, setFilterModalOpen] = useState(false);
    const isMobile = useMediaQuery({maxWidth: 1000});
    const isNarrowDesktop = useMediaQuery({minWidth: 1001, maxWidth: 1049});
    const canViewPage = canSeeTimeBankReportPage(profile);
    const canViewOtherEmployees = canSeeOtherTimeBank(profile);
    const canCreateOtherTransactions = canManageOtherTimeBank(profile);
    const canDeleteTransactions = canCreateOtherTransactions;
    const ownEmployee = useMemo<REmployees | null>(() => {
        if (!profile.id) {
            return null;
        }

        return employees.find((employee) => employee.id === profile.id)
            ?? {id: profile.id, name: profile.name, isDefault: false};
    }, [employees, profile.id, profile.name]);
    const filterEmployees = useMemo(
        () => (canViewOtherEmployees ? employees : ownEmployee ? [ownEmployee] : []),
        [canViewOtherEmployees, employees, ownEmployee]
    );

    const defaults = useMemo(() => getDefaultMonthRange(today), [today.valueOf()]);
    const selectedDates = useMemo(
        () => getSelectedDateRangeFromFilter(dateRangeFilter, defaults),
        [dateRangeFilter, defaults]
    );
    const calendarYears = useMemo(() => mapWorkCalendarYears(years), [years]);
    const selectedEmployeeIds = useMemo(
        () => {
            if (!canViewOtherEmployees) {
                return profile.id ? [profile.id] : [];
            }

            return propertyFilter.selectedEmployees?.map((employee) => employee.originalId as number) ?? [];
        },
        [canViewOtherEmployees, profile.id, propertyFilter.selectedEmployees]
    );
    const reportEmployees = useMemo<REmployees[]>(() => {
        if (!canViewOtherEmployees) {
            return ownEmployee ? [ownEmployee] : [];
        }

        if (selectedEmployeeIds.length < 1) {
            return employees;
        }

        const selectedIds = new Set(selectedEmployeeIds);
        return employees.filter((employee) => selectedIds.has(employee.id));
    }, [canViewOtherEmployees, employees, ownEmployee, selectedEmployeeIds]);
    const requestEmployeeIds = useMemo(
        () => {
            if (!canViewOtherEmployees) {
                return profile.id ? [profile.id] : [];
            }

            return selectedEmployeeIds;
        },
        [canViewOtherEmployees, profile.id, selectedEmployeeIds]
    );
    const selectedManualTransactionEmployeeId = canCreateOtherTransactions && selectedEmployeeIds.length === 1
        ? selectedEmployeeIds[0]
        : profile.id || undefined;
    const filteredBalances = useMemo(
        () => filterTimeBankBalances(balances, search),
        [balances, search]
    );
    const employeesLoading = canViewOtherEmployees && employees.length < 1;
    const pageLoading = loading || employeesLoading;
    const openingBalance = useMemo(
        () => filteredBalances.reduce((sum, row) => sum + row.openingBalance, 0),
        [filteredBalances]
    );
    const totalBalance = useMemo(
        () => filteredBalances.reduce((sum, row) => sum + row.periodBalance, 0),
        [filteredBalances]
    );
    const currentBalance = useMemo(
        () => filteredBalances.reduce((sum, row) => sum + row.balance, 0),
        [filteredBalances]
    );

    const from = selectedDates[0].format('YYYY-MM-DD');
    const to = selectedDates[1].format('YYYY-MM-DD');

    const loadReport = useCallback(async (targetEmployees: REmployees[], employeeIds: number[]) => {
        if (!canViewOtherEmployees && employeeIds.length < 1) {
            setBalances([]);
            return;
        }

        setLoading(true);
        setBalances([]);
        try {
            const employeeById = new Map(targetEmployees.map((employee) => [employee.id, employee]));
            const response = await HttpClient.timeBankApi.getReport(
                from,
                to,
                employeeIds
            );

            setBalances((response.data ?? []).map((row): TimeBankBalanceRow => {
                const employee = employeeById.get(row.employeeId);
                const employeeName = employee?.name ?? String(row.employeeId);
                const employeeTransactions = (row.transactions ?? [])
                    .map((transaction) => ({
                        ...transaction,
                        employeeId: row.employeeId,
                        employeeName,
                    }))
                    .sort((left, right) => {
                        const dateCompare = moment(left.date).valueOf() - moment(right.date).valueOf();

                        return dateCompare !== 0 ? dateCompare : left.id - right.id;
                    });
                const periodBalance = employeeTransactions.reduce(
                    (sum, transaction) => sum + transaction.hours,
                    0
                );
                const balance = row.balance ?? 0;

                return {
                    employeeId: row.employeeId,
                    employeeName,
                    balance,
                    openingBalance: balance - periodBalance,
                    periodBalance,
                    transactionCount: employeeTransactions.length,
                    transactions: employeeTransactions,
                };
            }).sort((a, b) => {
                const aPriority = a.periodBalance !== 0 || a.balance !== 0;
                const bPriority = b.periodBalance !== 0 || b.balance !== 0;

                if (aPriority !== bPriority) {
                    return aPriority ? -1 : 1;
                }

                return a.employeeName.localeCompare(b.employeeName);
            }));
        } finally {
            setLoading(false);
        }
    }, [canViewOtherEmployees, from, to]);

    const loadCalendarYears = useCallback(async (calendarId: number) => {
        const loadedYears = await Promise.all(
            [currentYear - 1, currentYear].map((year) =>
                dispatch(getWorkCalendar({workCalendarId: calendarId, year})).unwrap()
            )
        );

        setYears((currentYears) => {
            const nextYears = [...currentYears];
            loadedYears.forEach((loadedYear) => {
                if (!nextYears.find((year) => year.year === loadedYear.year)) {
                    nextYears.push(loadedYear);
                }
            });
            return nextYears;
        });
    }, [currentYear, dispatch]);

    useEffect(() => {
        if (!canViewPage) {
            return;
        }

        const initialize = async () => {
            if (employees.length < 1) {
                dispatch(getAllEmployees());
            }

            const primaryCalendar = workCalendars[0] ?? (await dispatch(getWorkCalendars()).unwrap())[0];
            if (primaryCalendar) {
                await loadCalendarYears(primaryCalendar.id);
            }
        };

        void initialize();
    }, [canViewPage, dispatch, employees.length, loadCalendarYears, workCalendars]);

    useEffect(() => {
        if (!canViewPage || employeesLoading) {
            return;
        }

        void loadReport(reportEmployees, requestEmployeeIds);
    }, [canViewPage, employeesLoading, loadReport, reportEmployees, requestEmployeeIds]);

    useEffect(() => {
        setIndicatorLoading(pageLoading);
        return () => {
            setIndicatorLoading(false);
        };
    }, [pageLoading]);

    useEffect(() => {
        if (!dateRangeFilter?.startDate || !dateRangeFilter?.endDate) {
            updateTimeTrackingDateRangeFilter({
                startDate: defaults.startDate.format('YYYY-MM-DD'),
                endDate: defaults.endDate.format('YYYY-MM-DD'),
            });
        }
    }, [dateRangeFilter?.endDate, dateRangeFilter?.startDate, defaults.endDate, defaults.startDate]);

    useEffect(() => {
        if (!isMobile) {
            setDateSelectorOpen(false);
            setFilterModalOpen(false);
        }
    }, [isMobile]);

    const handleDateRangeChange = useCallback((values: moment.Moment[]) => {
        updateTimeTrackingDateRangeFilter({
            startDate: values[0].format('YYYY-MM-DD'),
            endDate: values[1].format('YYYY-MM-DD'),
        });
    }, []);

    const handlePropertyFilterChanged = useCallback((value: TimeBankFilterState) => new Promise<void>((resolve) => {
        updateTimeTrackingPropertyFilter({
            selectedEmployees: value.selectedEmployees ?? [],
            selectedProjects: [],
            showAbsences: true,
        });
        resolve();
    }), []);

    const handleResetFilters = useCallback(() => {
        updateTimeTrackingPropertyFilter({
            selectedEmployees: [],
            selectedProjects: [],
            showAbsences: true,
        });
        setSearch('');
    }, []);

    const handleMobileFiltersApply = useCallback(async (value: TimeBankFilterState) => {
        await handlePropertyFilterChanged(value);
        setFilterModalOpen(false);
    }, [handlePropertyFilterChanged]);

    const handleMobileFiltersClear = useCallback(() => {
        handleResetFilters();
        setFilterModalOpen(false);
    }, [handleResetFilters]);

    const dateSelectorState = useMemo(() => ({
        open: isDateSelectorOpen,
        setOpen: setDateSelectorOpen,
    }), [isDateSelectorOpen]);
    const hasSelectedEmployees = selectedEmployeeIds.length > 0;
    const hasSearch = search.trim().length > 0;
    const hasActiveFilters = (canViewOtherEmployees && hasSelectedEmployees) || hasSearch;
    const canCreateTransaction = canCreateOtherTransactions;
    const mobileFiltersOpen = isMobile && isFilterModalOpen;
    const shouldShowCreateFooterButton = isMobile && !mobileFiltersOpen && canCreateTransaction;
    const pageBodyWrapperClassName = classNames(styles.pageBodyWrapper, {
        [styles.pageBodyWrapperWithFooter]: shouldShowCreateFooterButton,
    });

    const handleCreateTransaction = () => {
        navigate(NAVIGATION_ROUTES.CREATE_TIME_BANK_TRANSACTION, {state: {employeeId: selectedManualTransactionEmployeeId}});
    };

    const handleDeleteDialogClose = useCallback(() => {
        setTransactionPendingDelete(null);
    }, []);

    const handleDeleteDialogConfirm = useCallback(async () => {
        if (!transactionPendingDelete?.id) {
            return;
        }

        try {
            await HttpClient.timeBankApi.deleteManualTransaction(transactionPendingDelete.id);
            await loadReport(reportEmployees, requestEmployeeIds);
        } catch (error) {
            console.error(error);
        } finally {
            handleDeleteDialogClose();
        }
    }, [handleDeleteDialogClose, loadReport, reportEmployees, requestEmployeeIds, transactionPendingDelete]);

    if (!canViewPage) {
        return null;
    }

    const header = isMobile ? (
        <div className={classNames(costTopBarStyles.container, costTopBarStyles.mobileContainer)}>
            <div className={costTopBarStyles.mobileHeaderRow}>
                <TimeBankReportTitle className={costTopBarStyles.mobileTitle} />
                <div className={costTopBarStyles.mobileHeaderActions}>
                    <IconButton
                        className={classNames(styles.mobileIconButton, {
                            [styles.mobileIconButtonActive]: hasActiveFilters || isFilterModalOpen,
                            [styles.mobileIconButtonOpen]: isFilterModalOpen,
                        })}
                        size='small'
                        onClick={() => {
                            if (pageLoading) {
                                return;
                            }

                            setFilterModalOpen((value) => {
                                const next = !value;
                                if (next) {
                                    setDateSelectorOpen(false);
                                }
                                return next;
                            });
                        }}
                        disabled={pageLoading}
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                    >
                        <SVGS.FilterAltRounded />
                    </IconButton>
                </div>
            </div>
        </div>
    ) : (
        <div className={costTopBarStyles.container}>
            <Box className={costTopBarStyles.header}>
                <div className={classNames(costTopBarStyles.headerItem, costTopBarStyles.dateSelector)}>
                    <TimeBankReportTitle className={costTopBarStyles.headerText} />
                </div>
                <Box className={classNames(costTopBarStyles.dateSelector, costTopBarStyles.headerCalendar)}>
                    <ScheduleDateSelector
                        selectedDates={selectedDates}
                        onRangeSelected={handleDateRangeChange}
                        years={calendarYears}
                        minDate={today.clone().add(-365, 'days')}
                        maxDate={today.clone().endOf('month')}
                        allowedRangeTypes={ALLOWED_RANGE_TYPES}
                        disabled={loading}
                    />
                </Box>
                <Box gap={1} display='flex' height='100%' className={costTopBarStyles.headerItem}>
                    {canCreateTransaction ? (
                        <Button
                            className={costTopBarStyles.actionButton}
                            variant='loadMore'
                            onClick={handleCreateTransaction}
                            disabled={loading}
                        >
                            <SVGS.PlusIcon className={costTopBarStyles.icon} />
                        </Button>
                    ) : null}
                </Box>
            </Box>
        </div>
    );

    return (
        <>
            <PageWrapperV2 className={styles.pageScope}>
                <PageHeader>
                    {header}
                </PageHeader>
                <TimeTrackingPageBody justifyContent='flex-start' alignItems='stretch'>
                    <div className={pageBodyWrapperClassName}>
                        <div className={styles.pageBodyContent}>
                            {mobileFiltersOpen ? (
                                <TimeBankMobileFilterSheet
                                    filters={filterState}
                                    employees={filterEmployees}
                                    search={search}
                                    onSearchChange={setSearch}
                                    onApply={handleMobileFiltersApply}
                                    onClear={handleMobileFiltersClear}
                                    disabled={pageLoading}
                                    intl={intl}
                                    disableEmployeesFilter={!canViewOtherEmployees}
                                />
                            ) : null}

                            {isMobile && !mobileFiltersOpen ? (
                                <div className={styles.mobileDateRow}>
                                    <div className={styles.mobileDateSelector}>
                                        <ScheduleDateSelector
                                            selectedDates={selectedDates}
                                            onRangeSelected={handleDateRangeChange}
                                            years={calendarYears}
                                            minDate={today.clone().add(-365, 'days')}
                                            maxDate={today.clone().endOf('month')}
                                            allowedRangeTypes={ALLOWED_RANGE_TYPES}
                                            calendarState={dateSelectorState}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            ) : null}

                            {!mobileFiltersOpen ? <div className={styles.container}>
                                <Box className={styles.subheader}>
                                    <div className={styles.searchFilter}>
                                        {!isMobile ? (
                                            <div className={styles.filterActions}>
                                                <SearchInput
                                                    searchValue={search}
                                                    onChange={setSearch}
                                                    width={isNarrowDesktop ? '180px' : '260px'}
                                                    minWidth='82px'
                                                    disabled={loading}
                                                />
                                                {canViewOtherEmployees ? (
                                                    <TimeBankFilter
                                                        filters={filterState}
                                                        onChange={handlePropertyFilterChanged}
                                                        disabled={loading}
                                                        employees={filterEmployees}
                                                        intl={intl}
                                                    />
                                                ) : null}
                                                {hasActiveFilters ? (
                                                    <Button
                                                        className={styles.resetButton}
                                                        onClick={handleResetFilters}
                                                        disabled={loading}
                                                    >
                                                        <SVGS.CancelIcon className={styles.resetIcon} />
                                                    </Button>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className={styles.info}>
                                        <TimeBankTotals
                                            currentBalance={currentBalance}
                                            openingBalance={openingBalance}
                                            balance={totalBalance}
                                        />
                                    </div>
                                </Box>
                            </div> : null}

                            {!mobileFiltersOpen ? <div className={styles.tableSection}>
                                <div className={styles.tableWrap}>
                                    {isMobile ? (
                                        <TimeBankMobileView
                                            balances={filteredBalances}
                                            loading={pageLoading}
                                            intl={intl}
                                            canDeleteTransactions={canDeleteTransactions}
                                            onDeleteTransaction={setTransactionPendingDelete}
                                        />
                                    ) : (
                                        <BalancesTable
                                            balances={filteredBalances}
                                            loading={pageLoading}
                                            intl={intl}
                                            compactSourceColumn={isNarrowDesktop}
                                            canDeleteTransactions={canDeleteTransactions}
                                            onDeleteTransaction={setTransactionPendingDelete}
                                        />
                                    )}
                                </div>
                            </div> : null}
                        </div>
                    </div>
                </TimeTrackingPageBody>
                {shouldShowCreateFooterButton ? (
                    <PageFooter>
                        <CreateNewButtonV2 onClick={handleCreateTransaction} disabled={pageLoading} />
                    </PageFooter>
                ) : null}
            </PageWrapperV2>
            <DeleteDialog
                open={transactionPendingDelete !== null}
                onCancel={handleDeleteDialogClose}
                onConfirmDelete={handleDeleteDialogConfirm}
                deleteHeader={intl.formatMessage({id: Lx.General.DELETE})}
                deleteMessage='Delete time bank manual adjustment transaction?'
                title='time bank transaction'
            />
        </>
    );
}

function TimeBankTotals(props: {
    currentBalance: number;
    openingBalance: number;
    balance: number;
}) {
    return (
        <div className={classNames(totalStyles.total, styles.timeBankTotals)}>
            <TimeBankTotalPart label={<FormattedMessage id={Lx.TimeBank.CURRENT_BALANCE} />} value={props.currentBalance} />
            <TimeBankTotalPart label={<FormattedMessage id={Lx.TimeBank.OPENING_BALANCE} />} value={props.openingBalance} />
            <TimeBankTotalPart label={<FormattedMessage id={Lx.TimeBank.PERIOD_BALANCE} />} value={props.balance} />
        </div>
    );
}

function TimeBankTotalPart(props: { label: React.ReactNode; value: number }) {
    return (
        <div className={totalStyles.totalPart}>
            <p>{props.label}:</p>
            <span className={classNames(totalStyles.totalValue, {
                [styles.positive]: props.value > 0,
                [styles.negative]: props.value < 0,
            })}>
                {formatHours(props.value)}
            </span>
        </div>
    );
}

function TimeBankMobileFilterSheet(props: {
    filters: TimeBankFilterState;
    employees: REmployees[];
    search: string;
    onSearchChange: (value: string) => void;
    onApply: (filter: TimeBankFilterState) => Promise<void> | void;
    onClear: () => void;
    disabled: boolean;
    intl: IntlShape;
    disableEmployeesFilter: boolean;
}) {
    const [draft, setDraft] = useState<TimeBankFilterState>(props.filters);
    const employeeItems = useMemo(() => props.employees.map((employee) => ({
        id: employee.id,
        name: employee.name,
        originalId: employee.id,
    })), [props.employees]);

    useEffect(() => {
        setDraft(props.filters);
    }, [props.filters]);

    const handleApply = () => {
        void props.onApply({
            selectedEmployees: draft.selectedEmployees ?? [],
        });
    };

    return (
        <div className={styles.mobileFilterSheet}>
            <div className={styles.mobileFilterContent}>
                <SearchInput
                    searchValue={props.search}
                    onChange={props.onSearchChange}
                    width='100%'
                    disabled={props.disabled}
                />
                {!props.disableEmployeesFilter ? (
                    <div className={styles.mobileFilterSelectorBlock}>
                        <Selector
                            id='time-bank-mobile-filter-employees'
                            label={Lx.General.EMPLOYEES}
                            items={employeeItems}
                            name={Lx.General.EMPLOYEES}
                            selected={draft.selectedEmployees ?? []}
                            setSelectedValues={(values) => setDraft((state) => ({
                                ...state,
                                selectedEmployees: values,
                            }))}
                            intl={props.intl}
                            maxWidth={1000}
                            disabled={props.disabled}
                        />
                    </div>
                ) : null}
            </div>
            <Box className={styles.mobileFilterFooter}>
                <ActionCancelButtonsArea
                    idActionLocalization={Lx.General.APPLY}
                    idCancelLocalization={Lx.General.RESET_FILTER}
                    onCancel={props.onClear}
                    onAction={handleApply}
                    disabled={props.disabled}
                />
            </Box>
        </div>
    );
}

function TimeBankMobileView(props: {
    balances: TimeBankBalanceRow[];
    loading: boolean;
    intl: IntlShape;
    canDeleteTransactions: boolean;
    onDeleteTransaction: (transaction: TimeBankTransactionRow) => void;
}) {
    if (props.loading || props.balances.length === 0) {
        return null;
    }

    return (
        <div className={styles.mobileReportList}>
            {props.balances.map((balance) => (
                <TimeBankMobileSection
                    key={balance.employeeId}
                    balance={balance}
                    intl={props.intl}
                    canDeleteTransactions={props.canDeleteTransactions}
                    onDeleteTransaction={props.onDeleteTransaction}
                />
            ))}
        </div>
    );
}

function TimeBankMobileSection(props: {
    balance: TimeBankBalanceRow;
    intl: IntlShape;
    canDeleteTransactions: boolean;
    onDeleteTransaction: (transaction: TimeBankTransactionRow) => void;
}) {
    const [expanded, setExpanded] = useState(false);
    const toggle = () => setExpanded((state) => !state);
    const handleButtonClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.stopPropagation();
        toggle();
    };

    return (
        <div className={styles.mobileSection}>
            <div
                className={classNames(styles.mobileSectionHeader, {
                    [styles.mobileSectionHeaderCollapsed]: !expanded,
                })}
                role='button'
                tabIndex={0}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                <button
                    type='button'
                    className={styles.mobileSectionToggleButton}
                    onClick={handleButtonClick}
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                >
                    <SVGS.RightChevronIcon
                        className={classNames(styles.mobileSectionToggleIcon, {
                            [styles.mobileSectionToggleIconExpanded]: expanded,
                        })}
                    />
                </button>
                <span className={styles.mobileEmployeeName}>{props.balance.employeeName}</span>
            </div>

            {expanded ? (
                <div className={styles.mobileSectionContent}>
                    <TimeBankMobileTotalBar
                        currentBalance={props.balance.balance}
                        openingBalance={props.balance.openingBalance}
                        balance={props.balance.periodBalance}
                        intl={props.intl}
                    />
                    {props.balance.transactions.length > 0 ? (
                        <div className={styles.mobileCards}>
                            {props.balance.transactions.map((transaction) => (
                                <TimeBankMobileCard
                                    key={`${transaction.employeeId}-${transaction.id}`}
                                    transaction={transaction}
                                    intl={props.intl}
                                    canDeleteTransactions={props.canDeleteTransactions}
                                    onDeleteTransaction={props.onDeleteTransaction}
                                />
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function TimeBankMobileTotalBar(props: {
    currentBalance: number;
    openingBalance: number;
    balance: number;
    intl: IntlShape;
}) {
    const metrics = [
        {
            key: 'currentBalance',
            label: `${props.intl.formatMessage({id: Lx.TimeBank.CURRENT_BALANCE})}:`,
            value: formatTableHours(props.currentBalance) || '0',
            className: classNames({
                [styles.positive]: props.currentBalance > 0,
                [styles.negative]: props.currentBalance < 0,
                [styles.mobileMetricValueRegular]: props.currentBalance === 0,
            }),
        },
        {
            key: 'openingBalance',
            label: `${props.intl.formatMessage({id: Lx.TimeBank.OPENING_BALANCE})}:`,
            value: formatTableHours(props.openingBalance) || '0',
            className: classNames({
                [styles.positive]: props.openingBalance > 0,
                [styles.negative]: props.openingBalance < 0,
                [styles.mobileMetricValueRegular]: props.openingBalance === 0,
            }),
        },
        {
            key: 'balance',
            label: `${props.intl.formatMessage({id: Lx.TimeBank.PERIOD_BALANCE})}:`,
            value: formatTableHours(props.balance) || '0',
            className: classNames({
                [styles.positive]: props.balance > 0,
                [styles.negative]: props.balance < 0,
                [styles.mobileMetricValueRegular]: props.balance === 0,
            }),
        },
    ];

    return (
        <div className={styles.mobileTotalBar}>
            {metrics.map((metric, index) => (
                <div
                    key={metric.key}
                    className={classNames(styles.mobileMetric, {
                        [styles.mobileMetricWithDivider]: index % 3 !== 0,
                    })}
                >
                    <span className={styles.mobileMetricLabel}>{metric.label}</span>
                    <span className={classNames(styles.mobileMetricValue, metric.className)}>{metric.value}</span>
                </div>
            ))}
        </div>
    );
}

function TimeBankMobileCard(props: {
    transaction: TimeBankTransactionRow;
    intl: IntlShape;
    canDeleteTransactions: boolean;
    onDeleteTransaction: (transaction: TimeBankTransactionRow) => void;
}) {
    const transaction = props.transaction;
    const date = moment(transaction.date, 'YYYY-MM-DD', true);

    return (
        <div className={styles.mobileCard}>
            <div className={styles.mobileCardHeader}>
                <div className={styles.mobileCardMain}>
                    <span className={classNames(styles.mobileCardTitle, styles.mobileDateValue)}>
                        <span>{date.isValid() ? date.format('L') : transaction.date}</span>
                        {date.isValid() ? <span className={styles.mobileDateWeekday}>{date.format('dd')}</span> : null}
                    </span>
                    <span className={styles.mobileCardSubtitle}>
                        {sourceTypeLabels[transaction.sourceType] ?? transaction.sourceType}
                    </span>
                    <span className={styles.mobileCardSubtitle}>
                        {transactionTypeLabels[transaction.type] ?? transaction.type}
                    </span>
                    {transaction.comment ? (
                        <span className={classNames(styles.mobileCardSubtitle, styles.mobileCardComment)}>
                            {transaction.comment}
                        </span>
                    ) : null}
                </div>
                <div className={styles.mobileCardHours}>
                    <span className={styles.mobileMetricLabel}>{props.intl.formatMessage({id: Lx.TimeBank.HOURS})}:</span>
                    <span className={classNames(styles.mobileMetricValue, {
                        [styles.positive]: transaction.hours > 0,
                        [styles.negative]: transaction.hours < 0,
                        [styles.mobileMetricValueRegular]: transaction.hours === 0,
                    })}>{formatTableHours(transaction.hours) || '0'}</span>
                    {props.canDeleteTransactions && transaction.canDelete ? (
                        <div className={styles.mobileDeleteAction}>
                            <DeleteIconButton onClick={() => props.onDeleteTransaction(transaction)} />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function BalancesTable(props: {
    balances: TimeBankBalanceRow[];
    loading: boolean;
    intl: IntlShape;
    compactSourceColumn: boolean;
    canDeleteTransactions: boolean;
    onDeleteTransaction: (transaction: TimeBankTransactionRow) => void;
}) {
    const gridRef = React.useRef<GridRef>(null);
    const [isAnyRowExpanded, setIsAnyRowExpanded] = useState(false);
    const columns = useMemo<GridColumnProps[]>(
        () => createTimeBankColumns(props.compactSourceColumn, props.canDeleteTransactions, props.onDeleteTransaction),
        [props.canDeleteTransactions, props.compactSourceColumn, props.onDeleteTransaction]
    );
    const rows = useMemo<TimeBankGridRow[]>(() => createTimeBankRows(props.balances), [props.balances]);
    const groupBy = useMemo<GridGroupByProps>(() => createTimeBankGroupBy(props.canDeleteTransactions), [props.canDeleteTransactions]);
    const headerStyle = useMemo(() => buildTimeBankHeaderTemplate(props.canDeleteTransactions), [props.canDeleteTransactions]);
    const collapseLabel = props.intl.formatMessage({id: isAnyRowExpanded ? Lx.General.COLLAPSE_ALL : Lx.General.EXPAND_ALL});

    if (props.loading) {
        return null;
    }

    return (
        <div className={classNames(styles.gridWrapper, styles.gridWrapper)}>
            <div className={styles.customHeaderWrapper}>
                <div className={classNames(styles.customHeader, styles.customHeader)} style={headerStyle}>
                    <div className={classNames(styles.customHeaderCell, styles.collapseHeaderCell)}>
                        <CollapseButton
                            displayText={collapseLabel}
                            isExpanded={isAnyRowExpanded}
                            onExpandClick={() => gridRef.current?.collapseRows()}
                            containerClassName={styles.collapseButtonContainer}
                            displayTextClassName={styles.collapseButtonText}
                        />
                    </div>
                    <div className={classNames(styles.customHeaderCell, styles.headerRight)}>
                        {props.intl.formatMessage({id: Lx.TimeBank.CURRENT_BALANCE})}
                    </div>
                    <div className={classNames(styles.customHeaderCell, styles.headerRight)}>
                        {props.intl.formatMessage({id: Lx.TimeBank.OPENING_BALANCE})}
                    </div>
                    <div className={classNames(styles.customHeaderCell, styles.headerRight)}>
                        {props.intl.formatMessage({id: Lx.TimeBank.PERIOD_BALANCE})}
                    </div>
                    {props.canDeleteTransactions ? (
                        <div className={classNames(styles.customHeaderCell, styles.actionHeaderCell)} />
                    ) : null}
                </div>
            </div>
            {props.balances.length > 0 ? (
                <TimiDataGrid
                    hideHeader
                    columns={columns}
                    rows={rows}
                    groupBy={groupBy}
                    intl={props.intl}
                    className={classNames(styles.grid)}
                    ref={gridRef}
                    onAnyRowExpand={setIsAnyRowExpanded}
                />
            ) : null}
        </div>
    );
}

function createTimeBankRows(balances: TimeBankBalanceRow[]): TimeBankGridRow[] {
    return balances.flatMap<TimeBankGridRow>((balanceRow): TimeBankGridRow[] => {
        const employeeGroupKey = `${balanceRow.employeeName.toLowerCase()}__${balanceRow.employeeId}`;

        if (balanceRow.transactions.length < 1) {
            return [{
                id: `empty-${balanceRow.employeeId}`,
                transactionId: null,
                employeeGroupKey,
                balanceRow,
                isPlaceholder: true,
                employeeId: balanceRow.employeeId,
                employeeName: balanceRow.employeeName,
                date: '',
                hours: 0,
                type: TimeBankTransactionType.PutInto,
                sourceType: TimeBankTransactionSourceType.Migration,
                comment: '',
                created: '',
                updated: '',
                canDelete: false,
            }];
        }

        return [...balanceRow.transactions]
            .sort((left, right) => {
                const dateCompare = moment(left.date).valueOf() - moment(right.date).valueOf();

                return dateCompare !== 0 ? dateCompare : left.id - right.id;
            })
            .map((transaction) => ({
                ...transaction,
                id: `${transaction.employeeId}-${transaction.id}`,
                transactionId: transaction.id,
                employeeGroupKey,
                balanceRow,
                isPlaceholder: false,
            }));
    });
}

function createTimeBankColumns(
    compactSourceColumn = false,
    canDeleteTransactions = false,
    onDeleteTransaction?: (transaction: TimeBankTransactionRow) => void
): GridColumnProps[] {
    const columns: GridColumnProps[] = [
        {
            field: 'date',
            localizationKey: Lx.General.DATE,
            width: 120,
            cellRender: (field, row) => {
                const item = row as TimeBankGridRow;
                const date = moment(item.date, 'YYYY-MM-DD', true);

                if (item.isPlaceholder) {
                    return <div className={classNames(styles.cell, styles.ellipsisCell)} />;
                }

                if (!date.isValid()) {
                    return (
                        <div className={classNames(styles.cell, styles.ellipsisCell)}>
                            {item.date}
                        </div>
                    );
                }

                return (
                    <div className={classNames(styles.cell, styles.dateCell)}>
                        <span>{date.format('L')}</span>
                        <span className={styles.dateWeekday}>{date.format('dd')}</span>
                    </div>
                );
            },
        },
        {
            field: 'type',
            localizationKey: Lx.General.TYPE,
            width: 160,
            cellRender: (field, row) => {
                const item = row as TimeBankGridRow;
                const label = item.isPlaceholder ? '' : transactionTypeLabels[item.type] ?? item.type;
                return (
                    <div className={classNames(styles.cell, styles.typeCell, styles.ellipsisCell)}>
                        <span className={styles.ellipsisText} title={label}>
                            {label}
                        </span>
                    </div>
                );
            },
        },
        {
            field: 'sourceType',
            localizationKey: Lx.TimeBank.SOURCE,
            width: compactSourceColumn ? 130 : 160,
            cellRender: (field, row) => {
                const item = row as TimeBankGridRow;
                const label = item.isPlaceholder ? '' : sourceTypeLabels[item.sourceType] ?? item.sourceType;

                return (
                    <div className={classNames(styles.cell, styles.sourceTypeCell, styles.ellipsisCell)}>
                        <span className={styles.ellipsisText} title={label}>
                            {label}
                        </span>
                    </div>
                );
            },
        },
        {
            field: 'comment',
            localizationKey: Lx.Schedule.TIMESHIFT_NOTE,
            flex: {grow: 2},
            cellRender: (field, row) => {
                const item = row as TimeBankGridRow;
                return (
                    <div className={classNames(styles.cell, styles.noteCell, styles.scrollCell)}>
                        {item.isPlaceholder ? '' : item.comment || ''}
                    </div>
                );
            },
        },
        {
            field: 'hours',
            localizationKey: Lx.TimeBank.HOURS,
            width: 120,
            cellRender: (field, row) => {
                const item = row as TimeBankGridRow;
                return (
                    <div className={classNames(styles.cell, styles.hoursCell, {
                        [styles.positive]: item.hours > 0,
                        [styles.negative]: item.hours < 0,
                    })}>
                        {item.isPlaceholder ? '' : formatTableHours(item.hours)}
                    </div>
                );
            },
        },
    ];

    if (canDeleteTransactions) {
        columns.push({
            field: 'deleteButton',
            width: 56,
            hideHeaderContent: true,
            cellRender: (field, row) => {
                const item = row as TimeBankGridRow;
                if (item.isPlaceholder || !item.canDelete || !item.transactionId) {
                    return <div className={classNames(styles.cell, styles.deleteActionCell)} />;
                }

                const transactionId = item.transactionId;
                return (
                    <div className={classNames(styles.cell, styles.deleteActionCell)}>
                        <DeleteIconButton
                            onClick={() => onDeleteTransaction?.({
                                ...item,
                                id: transactionId,
                            })}
                        />
                    </div>
                );
            },
        });
    }

    return columns;
}

function createTimeBankGroupBy(showDeleteColumn: boolean): GridGroupByProps {
    return {
        rowGroupingModel: ['employeeGroupKey'],
        groupByRenderer: (groupField, groupFieldValue, value) => {
            const balanceRow = (value[0] as TimeBankGridRow).balanceRow;
            return <TimeBankGroupRow row={balanceRow} showDeleteColumn={showDeleteColumn} />;
        },
        isGroupHasValues: (values) => values.some((value) => !(value as TimeBankGridRow).isPlaceholder),
    };
}

function buildTimeBankHeaderTemplate(showDeleteColumn: boolean): React.CSSProperties {
    return {
        gridTemplateColumns: showDeleteColumn
            ? 'minmax(0, 1fr) repeat(3, 120px) 56px'
            : 'minmax(0, 1fr) repeat(3, 120px)',
    };
}

function TimeBankGroupRow(props: { row: TimeBankBalanceRow; showDeleteColumn: boolean }) {
    const row = props.row;

    return (
        <div
            className={classNames(styles.groupRow, {
                [styles.groupRowWithActions]: props.showDeleteColumn,
            })}
        >
            <div className={styles.groupEmployeeName}>{row.employeeName}</div>
            <TimeBankGroupMetric value={row.balance} />
            <TimeBankGroupMetric value={row.openingBalance} />
            <TimeBankGroupMetric value={row.periodBalance} />
            {props.showDeleteColumn ? <div className={styles.groupActionSpacer} /> : null}
        </div>
    );
}

function TimeBankGroupMetric(props: { value: number }) {
    return (
        <div className={classNames(styles.groupMetric, {
            [styles.positive]: props.value > 0,
            [styles.negative]: props.value < 0,
        })}>
            {formatGroupHours(props.value)}
        </div>
    );
}
